import { Button, Form, notification, Checkbox } from "antd";
import React, { useEffect, useState } from "react";
import { injectIntl } from "react-intl";
import { httpService } from "../../../base/services/httpService.service";
import { useAuthSore } from "../authStore";
import { RegisterHeader } from "./components/createProfile/RegisterHeader";
import { Link, useSearchParams } from "react-router-dom";
import RegisterStep2 from "./RegisterStep2";
import RegisterStep3 from "./RegisterStep3";
import TermsConditionsModal from "../../../components/TermsConditionsModal";
import ConfirmationCodeModal from "./Confirmation";
import AccountDetails from "./components/accountDetails/AccountDetails";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
   clearRegistration,
   getProfileFormValues,
   getRegisteringAccount,
   getRegisteringPlan,
   hasConfirmedAccount,
   isAwaitingConfirmation,
   saveRegisteredIdentity,
   saveRegisteringAccount,
   startAwaitingConfirmation
} from "./registrationSession";
import {
   deriveCountryId,
   deriveOrganizationName,
   deriveOrganizationTypeId
} from "./registrationDefaults";

import "../style.scss";

// The package comes first, then the profile. Picking a plan needs no account —
// the packets are public — so it is what a sign up opens on. Filling the profile
// in is what creates the account and sends the code out; confirming that code
// is what opens the review.
//
//   package     profile + confirmation code     review & pay
//
// Either order works for the account itself: the level an organization is
// registered under is set by the subscription call, which is where the package
// is actually bought (see RegisterStep3), not by /Account/register.

const STEP_PACKAGE = "package";
const STEP_PROFILE = "profile";
const STEP_REVIEW = "review";

// The steps in order, which is all the rest of the flow wants of them: the
// aside stands down for the package step and the header offers the way to log
// in on the two steps before an account exists (AuthLayout, RegisterHeader).
const STEP_ORDER = [STEP_PACKAGE, STEP_PROFILE, STEP_REVIEW];

// Which step the registration is on is kept in the address, so a reload, a
// restored tab and the browser's own back and forward buttons all land where
// the flow was left rather than at the top of it.
//
// The address is not trusted on its own, though: it is typed, pasted and
// arrived at from history, and a step is only as real as what the session
// behind it holds. A profile step needs a plan to register for; a review step
// needs that plan and an account that exists with its address proven.
//
// Nor is a later step ever assumed. The bare address is the start of the flow,
// the package step, which is what "Sign up" links to. What a tab part way
// through was doing is not lost by landing there: the plan it picked is still
// picked, and Continue carries it on to wherever the registration had got to.
// The login page sends an account that never subscribed to the package step
// too, and from there it goes straight on to the review — its profile is
// already on the server.
const resolveStep = (requested) => {
   const hasPlan = Boolean(getRegisteringPlan());

   if (requested === STEP_REVIEW && hasPlan && hasConfirmedAccount()) return STEP_REVIEW;
   if ((requested === STEP_PROFILE || requested === STEP_REVIEW) && hasPlan) return STEP_PROFILE;

   return STEP_PACKAGE;
};

function Registration() {
   //zustand
   const setRegistStep = useAuthSore((state) => state.setRegistStep);
   const setcurrentOrgId = useAuthSore((state) => state.setcurrentOrgId);
   const [load, setLoad] = useState(false);

   //zustand
   const [form] = Form.useForm();

   const [searchParams, setSearchParams] = useSearchParams();
   const step = resolveStep(searchParams.get("step"));

   // Neither is asked for on the form any more. Both are here because the
   // account still carries them and they are filled in from what the form does
   // ask for — see registrationDefaults.js.
   const [organizationType, setOrganizationType] = useState();
   const [countries, setCountries] = useState();
   const [shouldDisable, setShouldDisable] = useState(() => Boolean(getRegisteringAccount()));

   // A reload in the middle of confirming is put back where it left off: the
   // code is already in the inbox, so the modal is what the registration needs.
   const [isModaOpen, setIsModaOpen] = useState(isAwaitingConfirmation);
   // When creating the account had its code mailed, for the modal to count
   // its five minutes down. Not known after a reload.
   const [codeSentAt, setCodeSentAt] = useState(null);
   const [modalVisible, setModalVisible] = useState(false);
   const [promoCode, setPromoCode] = useState("");

   const { executeRecaptcha } = useGoogleReCaptcha();

   const isConfirmed = hasConfirmedAccount();
   const awaitingConfirmation = !isConfirmed && isAwaitingConfirmation();

   // Moving between steps is a move through history, so back and forward walk
   // the flow. The package step is the address with nothing on it, which is
   // what a sign up is linked to.
   const goToStep = (next, { replace = false } = {}) =>
      setSearchParams(next === STEP_PACKAGE ? {} : { step: next }, { replace });

   // An address that asks for a step the session cannot back is corrected in
   // place — a link opened in a fresh tab, a forward button into a registration
   // that has since been started over — so what is shown and what is in the bar
   // never disagree.
   useEffect(() => {
      const requested = searchParams.get("step") ?? STEP_PACKAGE;
      if (requested !== step) goToStep(step, { replace: true });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [step, searchParams]);

   // The links sit inside the checkbox's own label, so opening the terms must
   // not also tick the box the terms are about.
   const openTermsModal = (e) => {
      e?.preventDefault();
      e?.stopPropagation();
      setModalVisible(!modalVisible);
   };

   // No level on this any more: an organization is registered before it has
   // picked a package, and the package is what says which level it is. The
   // subscription call carries it now.
   //
   // The five fields the form asks for are sent as they were typed. The
   // organization name, the country and the organization type are filled in
   // from them rather than asked for — see registrationDefaults.js — and the
   // address block is gone with the fields that fed it: Stripe collects the
   // billing address on the card form, which is the only place it was ever
   // going to be used.
   const buildRegistrationPayload = (values) => {
      // The organization's country and the person's own are the same one at
      // sign up: there is a single account behind both.
      const countryId = deriveCountryId(values.phone, countries);

      return {
         adminFullName: values.fullname,
         phoneNumber: values.phone,
         email: values.email,
         password: values.password,
         confirmPassword: values.confirm,
         organizationName: deriveOrganizationName(values.fullname),
         typeId: deriveOrganizationTypeId(organizationType),
         countryId,
         userCountryId: countryId
      };
   };

   // Submitting the profile is what creates the account.
   const onFinish = async (values) => {
      // Nothing to sign the request with yet. Said out loud, because a silent
      // return here is a button that looks broken.
      if (!executeRecaptcha) {
         openNotificationWithIcon(
            "warning",
            "Warning",
            "The security check is still loading. Please try again in a moment."
         );
         return;
      }

      // The account is already on the server and this form is only showing what
      // was registered. Registering it a second time is what the guard is for:
      // a stray submit — the enter key in a field, a double click that beat the
      // first response back — would otherwise be answered with an email that is
      // already in use.
      if (isConfirmed || awaitingConfirmation || load) return;

      setLoad(true);

      const recaptchaToken = await executeRecaptcha("login");

      await httpService
         .post(
            "/Account/register",
            buildRegistrationPayload(values),
            (res) => {
               saveSessions(values, res);
               setCodeSentAt(Date.now());
               openNotificationWithIcon(
                  "warning",
                  "Warning",
                  "Please confirm the code in your email"
               );
               setIsModaOpen(true);
               setLoad(false);
            },
            (errorData) => {
               setLoad(false);

               // A refusal that came back on a 200 has already been shown by the
               // response interceptor. Anything else — the 400 an email already
               // in use comes back as — reaches here unreported, and without
               // this would leave the button looking like it did nothing.
               if (errorData?.isSuccess === false) return;

               const failure =
                  errorData?.response?.data?.errors?.[0] ?? errorData?.response?.data?.message;

               openNotificationWithIcon(
                  "error",
                  "Error",
                  failure ?? "We couldn't create the account. Please try again."
               );
            },
            undefined,
            {
               "g-recaptcha-response": recaptchaToken
            }
         )
         .catch(() => {
            // Already reported in the callback above; the rejection is not news.
         });
   };

   // An account that already exists has nothing left to do on the profile step
   // — its details are on the server and its address is proven — so its plan
   // goes straight to the review. Any other is created next.
   const confirmPlan = () => {
      goToStep(isConfirmed ? STEP_REVIEW : STEP_PROFILE);
   };

   const handleCloseModal = () => {
      setIsModaOpen(false);
   };

   // The form is filled in and locked because an account was created from it,
   // and whoever is looking at it now wants to register a different one. The
   // account that was created stays where it is — this is the browser letting
   // go of it, not the platform. Its email is confirmed from the login page,
   // which mails a fresh code out to anyone who signs in unconfirmed. The plan
   // goes with the rest, so the new registration starts from the packages.
   const startNewRegistration = (e) => {
      e?.preventDefault();
      clearRegistration();
      setcurrentOrgId(null);
      form.resetFields();
      setShouldDisable(false);
      setIsModaOpen(false);
      setCodeSentAt(null);
      goToStep(STEP_PACKAGE, { replace: true });
   };

   // The account exists and the address behind it is proven — the modal has
   // already recorded that — which is what the review was waiting on.
   const confirmCode = () => {
      openNotificationWithIcon("success", "Success", "Account created successfully");
      goToStep(STEP_REVIEW, { replace: true });
   };

   const [api, contextHolder] = notification.useNotification();

   const openNotificationWithIcon = (type, msg, desc) => {
      api[type]({
         message: msg,
         description: desc
      });
   };

   const saveSessions = (values, res) => {
      setLoad(false);
      // The profile is on the server now, so the form behind the modal stops
      // taking edits that would go nowhere.
      setShouldDisable(true);
      setcurrentOrgId(res.data.organizationId);
      saveRegisteredIdentity({
         organizationId: res.data.organizationId,
         userId: res.data.userId
      });
      // The code has gone out and nothing has come back yet. Written before the
      // form's own data, so a reload at any point from here on finds the
      // registration waiting on its email rather than free to pick a package.
      startAwaitingConfirmation();
      saveRegisteringAccount(values);
   };

   useEffect(() => {
      setRegistStep(STEP_ORDER.indexOf(step));
   }, [step, setRegistStep]);

   // What was registered is filled back in whenever the profile step is shown,
   // so a reload, a back button and the step's own "go back to your details"
   // all find the form as it was left rather than empty.
   useEffect(() => {
      if (step !== STEP_PROFILE) return;

      const values = getProfileFormValues();
      if (!values) return;

      form.setFieldsValue(values);
      setShouldDisable(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [step]);

   // Both lists are what the two fields the form no longer asks for are filled
   // in from, so they are fetched for the payload rather than for a select. A
   // list that never arrives leaves its field out of the payload — the register
   // endpoint asks for neither — so a sign up still goes through.
   useEffect(() => {
      httpService.get(
         `/Account/getOrganizationTypes`,
         (res) => setOrganizationType(res.data),
         () => {}
      );

      httpService.get(
         `/Languages/getAllCountries`,
         (res) => setCountries(res.data),
         () => {}
      );
   }, []);

   const onChangePromoCode = (value) => {
      setPromoCode(value);
   };

   return (
      <>
         {contextHolder}
         <div className="create-header">
            <RegisterHeader step={STEP_ORDER.indexOf(step)} />
         </div>

         <div className="register-page">
            <ConfirmationCodeModal
               isModaOpen={isModaOpen}
               handleClose={handleCloseModal}
               confirmCode={confirmCode}
               email={getRegisteringAccount()?.email}
               codeSentAt={codeSentAt}
            />
            <Form
               onFinish={onFinish}
               className="register-form"
               form={form}
               name="basic"
               autoComplete="off"
            >
               {step === STEP_PROFILE && (
                  <>
                     <div className="create-section">
                        <div className="register-back-link" onClick={() => goToStep(STEP_PACKAGE)}>
                           <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="17"
                              height="17"
                              viewBox="0 0 17 17"
                              fill="none"
                           >
                              <path
                                 d="M14.7358 8.2339L2.73584 8.2339M2.73584 8.2339L8.10255 2.86719M2.73584 8.2339L8.10255 13.6006"
                                 stroke="#5770CB"
                                 strokeLinecap="round"
                                 strokeLinejoin="round"
                              />
                           </svg>{" "}
                           Go back to your package
                        </div>
                        <div className="register-adminstrator-information">
                           <AccountDetails shouldDisable={shouldDisable} />
                        </div>{" "}
                        <div>
                           {/* The terms are a field of the form, not a note beside
                               it: registering without them is what the validator
                               stops, so the account is never created unticked.

                               Once the account exists there is nothing left to
                               agree to — the terms were accepted when it was
                               created — and a rule left on a disabled, unticked
                               box would fail a form nobody can fix. */}
                           <Form.Item
                              name="acceptTerms"
                              valuePropName="checked"
                              rules={[
                                 {
                                    validator: (_, checked) =>
                                       checked || shouldDisable
                                          ? Promise.resolve()
                                          : Promise.reject(
                                               new Error(
                                                  "Please read and agree with the Terms of Service, Privacy Policy and Cookie Policy to continue."
                                               )
                                            )
                                 }
                              ]}
                           >
                              <Checkbox className="m-1" disabled={shouldDisable}>
                                 <span style={{ marginTop: "2px" }}>
                                    I have read and agreed with the{" "}
                                    <Link
                                       to="#"
                                       onClick={openTermsModal}
                                       rel="noopener noreferrer"
                                       style={{
                                          color: "#5492FF",
                                          textDecoration: "underline",
                                          margin: "0 4px"
                                       }}
                                    >
                                       Terms of Service, Privacy Policy
                                    </Link>
                                    and{" "}
                                    <Link
                                       to="#"
                                       onClick={openTermsModal}
                                       rel="noopener noreferrer"
                                       style={{
                                          color: "#5492FF",
                                          textDecoration: "underline",
                                          margin: "0 4px"
                                       }}
                                    >
                                       Cookie Policy.
                                    </Link>
                                 </span>
                              </Checkbox>
                           </Form.Item>
                        </div>{" "}
                        <div className="register-button-group" style={{ marginTop: "5%" }}>
                           <TermsConditionsModal visible={modalVisible} onCancel={openTermsModal} />

                           {/* An account that already exists is only passing
                               through on its way to the review step: it skips
                               both a second registration and the validation its
                               disabled fields would fail.

                               One whose code is still outstanding has the same
                               nothing to submit, but the review is not what it
                               continues to — the modal it left is. */}
                           {awaitingConfirmation ? (
                              <Form.Item>
                                 <Button
                                    onClick={() => setIsModaOpen(true)}
                                    className="kapSurveyPrimaryButton"
                                 >
                                    Confirm your email
                                 </Button>
                              </Form.Item>
                           ) : isConfirmed ? (
                              <Form.Item>
                                 <Button
                                    onClick={() => goToStep(STEP_REVIEW)}
                                    className="kapSurveyPrimaryButton"
                                 >
                                    Continue
                                 </Button>
                              </Form.Item>
                           ) : (
                              <Form.Item>
                                 <Button
                                    htmlType="submit"
                                    className="kapSurveyPrimaryButton"
                                    disabled={load}
                                    loading={load}
                                 >
                                    Continue
                                 </Button>
                              </Form.Item>
                           )}
                        </div>
                        {/* A form that is locked to an account somebody else
                            started is a dead end without this: the fields take
                            no edits and the email is spoken for, so registering
                            a different one has to begin by letting this one go. */}
                        {(awaitingConfirmation || isConfirmed) && (
                           <div style={{ marginTop: "16px" }}>
                              Registering a different organization?{" "}
                              <Link
                                 to="#"
                                 onClick={startNewRegistration}
                                 style={{
                                    color: "#5492FF",
                                    textDecoration: "underline",
                                    margin: "0 4px"
                                 }}
                              >
                                 Start over
                              </Link>
                           </div>
                        )}
                        {/* A registration belongs to the tab it was started in.
                            Closing that tab does not undo the account it
                            created, so somebody coming back to an empty form
                            with an email that is already spoken for is told
                            where the rest of it went. */}
                        {!awaitingConfirmation && !isConfirmed && (
                           <div style={{ marginTop: "16px" }}>
                              Started signing up already?{" "}
                              <a
                                 href="/auth/login"
                                 style={{
                                    color: "#5492FF",
                                    textDecoration: "underline",
                                    margin: "0 4px"
                                 }}
                              >
                                 Log in
                              </a>
                              to pick up where you left off.
                           </div>
                        )}
                     </div>
                  </>
               )}
            </Form>

            {step === STEP_PACKAGE && (
               <RegisterStep2 onConfirmPlan={confirmPlan} />
            )}

            {step === STEP_REVIEW && (
               <RegisterStep3
                  promoCode={promoCode}
                  onChangePromoCode={onChangePromoCode}
                  onBack={() => goToStep(STEP_PACKAGE)}
               />
            )}
         </div>
      </>
   );
}

export default injectIntl(Registration);
